import {
	createSlice,
	createEntityAdapter,
	createSelector,
	createAsyncThunk,
} from "@reduxjs/toolkit";
import { answerRemoved } from "./answersSlice";
import { deleteAttachmentsByQuestionId } from "./attachmentsSlice";
import { formulaEdited } from "./formulasSlice";

const rowsAdapter = createEntityAdapter({ sortComparer: (a, b) => a - b });

const initialState = rowsAdapter.getInitialState({
	status: "starting",
	error: null,
});

export const deleteRowAndAnswers = createAsyncThunk(
	"row/deleteRowAndAnswers",
	async (row, { getState, dispatch, rejectWithValue }) => {
		//grab all state we need for formula calculations
		const { dependencies, formulas } = getState();
		try {
			//deleting a row requires the deletion of all related answers
			for (const [key, value] of Object.entries(row)) {
				//ignore row properties that relate to the row itself, and not the answers inside it
				if (!["id", "questionId", "sequence"].includes(key)) {
					//simple removal of answer entity
					dispatch(answerRemoved(value));
					dispatch(deleteAttachmentsByQuestionId(value));

					//returns an array of formulas that depend on the value that just changed
					let dependentFormulas =
						dependencies.entities[key]?.formulas;

					//each formula will have its valuesByRow deleted for this row
					if (dependentFormulas && dependentFormulas.length > 0) {
						dependentFormulas.forEach((d) => {
							//get all of the dependencies of this formula
							const formula = formulas.entities[d];
							//edit the formula if it previously held a value
							if (
								formula?.valuesByRow &&
								formula.valuesByRow.hasOwnProperty(row.id)
							) {
								const { [row.id]: _, ...otherProps } =
									formula.valuesByRow;
								dispatch(
									formulaEdited({
										...formula,
										valuesByRow: otherProps,
										error: null,
									})
								);
							}
						});
					}
				}
			}
			//delete the row itself
			dispatch(rowRemoved(row.id));
		} catch (error) {
			console.log(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
			});
		}
	}
);

const rowsSlice = createSlice({
	name: "rows",
	initialState,
	reducers: {
		//declaring this reducer generates action types/creators
		rowStatusEdited(state, action) {
			state.status = action.payload;
		},
		rowAdded: rowsAdapter.addOne,
		rowRemoved: rowsAdapter.removeOne,
		rowUpdated: rowsAdapter.updateOne,
		rowsUpdated: rowsAdapter.updateMany,
		rowsReceived(state, action) {
			rowsAdapter.setAll(state, action.payload);
		},
	},
});

//actions to be imported by components
export const {
	rowStatusEdited,
	rowAdded,
	rowRemoved,
	rowUpdated,
	rowsUpdated,
	rowsReceived,
} = rowsSlice.actions;

export const { selectAll: selectAllRows, selectById: selectRowById } =
	rowsAdapter.getSelectors((state) => state.rows);

//commonly used selectors
export const selectRowsByQuestionId = createSelector(
	[selectAllRows, (state, questionId) => questionId],
	(rows, questionId) => rows.filter((row) => row.questionId === questionId)
);

export const selectSequence = createSelector(
	[selectAllRows],
	(rows) => rows.length - 1
);

export const selectRowsError = (state) => state.rows.error;

export const selectRowsStatus = (state) => state.rows.status;

export default rowsSlice.reducer;
